import {Plugins} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  ExpandedQueryPath,
  FieldDef,
  QueryContext,
  QueryPath,
} from '../../types';
import {weakMemo} from '@packages/utilities/memo';
import {parseQueryPath} from '../../utils/QueryPathUtils';
import {getQueryPathFragments} from '../../utils/getQueryPathFragments';
import {getDescendants} from '@packages/utilities/platform';

export type ElementAnalysis = {
  absoluteQueryPaths: QueryPath[];
  dataPathSubTrees: DataPathSubTrees;
};

type AnalyzeElement = (
  plugins: Plugins.PluginsType
) => (
  queryContext: QueryContext
) => (element: ExpressionElement) => ElementAnalysis;

export const analyzeElement: AnalyzeElement = weakMemo((plugins) =>
  weakMemo((prevQueryContext) =>
    weakMemo((element) => {
      const queryContext = prevQueryContext.update(element);
      const {descendantQueryPaths} = queryContext;

      /*
      We maintain `absoluteQueryPaths`, because `descendantQueryPaths`
      allows plugins to specify to query paths for nodes that do not
      necessarily depend on queryPathPrefix.
    */
      const absoluteQueryPaths: QueryPath[] = [
        ...(descendantQueryPaths[element.id] || []),
      ];

      const dataPathSubTrees = new DataPathSubTrees();

      getDescendants(element).forEach((child) => {
        const {
          dataPathSubTrees: childDataPathSubTrees,
          absoluteQueryPaths: childAbsoluteQueryPaths,
        } = analyzeElement(plugins)(queryContext)(child);

        absoluteQueryPaths.push(...childAbsoluteQueryPaths);
        dataPathSubTrees.merge(childDataPathSubTrees);
      });

      dataPathSubTrees.grow(element, queryContext, plugins);

      return {absoluteQueryPaths, dataPathSubTrees};
    })
  )
);

// Slot defaults are analyzed alongside the render they belong to, so their
// results have to fold into one ingestion under the owning component's key.
export function mergeElementAnalyses(
  analyses: ElementAnalysis[]
): ElementAnalysis {
  const absoluteQueryPaths: QueryPath[] = [];
  const dataPathSubTrees = new DataPathSubTrees();

  for (const analysis of analyses) {
    absoluteQueryPaths.push(...analysis.absoluteQueryPaths);
    dataPathSubTrees.merge(analysis.dataPathSubTrees);
  }

  return {absoluteQueryPaths, dataPathSubTrees};
}

const createDataPathIndexer = (
  dataPaths: {[elementId: string]: ExpandedQueryPath},
  dataPath: string
) => {
  const splitDataPath =
    dataPath === EMPTY_DATA_PATH_PLACEHOLDER ? [] : dataPath.split('.');
  return (node: TreeNode) => {
    dataPaths[node.id] = splitDataPath;
  };
};

const createQueryLevelAccumulator =
  (queryLevels: [QueryPath[], QueryPath[]], queryPathPrefix: QueryPath) =>
  (node: TreeNode) => {
    const {queryPathFragments} = node;
    for (let i = 0, length = queryPathFragments.length; i < length; i++) {
      const queryPathFragment = queryPathFragments[i];
      if (queryPathFragment) {
        const queryPath =
          queryPathPrefix.length > 0
            ? queryPathPrefix.concat(queryPathFragment)
            : queryPathFragment;
        addQueryPathToQueryLevels(queryLevels, queryPath);
      }
    }
  };

const isFunction = (thing: FieldDef) => typeof thing === 'function';

const addQueryPathToQueryLevels = (
  queryLevels: [QueryPath[], QueryPath[]],
  queryPath: QueryPath
) => {
  queryLevels[queryPath.some(isFunction) ? 1 : 0].push(queryPath);
};

export function getDataPathsAndQueryLevels(
  dataPathSubTrees: DataPathSubTrees,
  absoluteQueryPaths: QueryPath[]
): {
  dataPaths: {[elementId: string]: ExpandedQueryPath};
  queryLevels: [QueryPath[], QueryPath[]];
} {
  const dataPaths: {[elementId: string]: ExpandedQueryPath} = {};
  const queryLevels: [QueryPath[], QueryPath[]] = [[], []];

  const _dataPathSubtrees = dataPathSubTrees.trees;

  for (const dataPath in _dataPathSubtrees) {
    const dataPathTreeNodes = _dataPathSubtrees[dataPath];
    if (dataPathTreeNodes) {
      const recordDataPath = createDataPathIndexer(dataPaths, dataPath);

      dataPathTreeNodes.forEach((dataPathTreeNode) => {
        const {queryPathPrefix, subTree} = dataPathTreeNode;
        const recordQueryPaths = createQueryLevelAccumulator(
          queryLevels,
          queryPathPrefix
        );
        // do not accumulate query levels if data path is empty
        const callbacks =
          dataPath === EMPTY_DATA_PATH_PLACEHOLDER
            ? [recordDataPath]
            : [recordDataPath, recordQueryPaths];

        treeForEach(subTree, callbacks);
      });
    }
  }

  for (let i = 0; i < absoluteQueryPaths.length; i++) {
    const absoluteQueryPath = absoluteQueryPaths[i];
    if (absoluteQueryPath) {
      addQueryPathToQueryLevels(queryLevels, absoluteQueryPath);
    }
  }

  return {dataPaths, queryLevels};
}

const treeForEach = (
  tree: {
    children: Array<TreeNode>;
    id: string;
    queryPathFragments: Array<QueryPath>;
  },
  callbacks: Array<(node: TreeNode) => void>
) => {
  const walkTree = (
    node:
      | TreeNode
      | {
          children: Array<TreeNode>;
          id: string;
          queryPathFragments: Array<QueryPath>;
        }
  ) => {
    callbacks.forEach((fn) => fn(node));
    node.children.forEach(walkTree);
  };
  walkTree(tree);
};

const parseDataPath = parseQueryPath(null);
const EMPTY_DATA_PATH_PLACEHOLDER = 'EMPTY_DATA_PATH_PLACEHOLDER';

type RootNode = {
  queryPathPrefix: QueryPath;
  subTree: TreeNode;
};

type TreeNode = {
  id: string;
  children: TreeNode[];
  queryPathFragments: QueryPath[];
};

/*
  The `dataPathSubTrees` is a data structure that holds the necessary
  information to derive `dataPaths` and `queryLevels` with proper
  memoization, but without the need to copy large arrays or objects.

  It divides the tree starting at `node` into subtrees based on
  `queryPathPrefix`. Each subtree is a minimal representation of the
  corresponding element subtree, whose dynamic binding paths can
  be formed by combining node binding data and `queryPathPrefix`.

  By using a tree representation, we can "grow" the subtrees from leaf
  to root by nesting references, like we do below, and maintain a
  sufficient level of mutation safety for the memoized return values
  we build upon while recursing.
*/
class DataPathSubTrees {
  trees: {
    [dataPath: string]: RootNode[];
  } & {EMPTY_DATA_PATH_PLACEHOLDER?: RootNode[]};

  constructor() {
    this.trees = {};
  }

  /*
    Merges `other.trees` into this.trees

    WARNING: This method mutates the object instance!
  */
  merge(other: DataPathSubTrees) {
    for (const dataPath in other.trees) {
      const dataPathRoots = other.trees[dataPath];
      if (dataPathRoots) {
        if (!this.trees[dataPath]) {
          this.trees[dataPath] = [];
        }
        this.trees[dataPath].push(...dataPathRoots);
      }
    }
  }

  /*
    Grows `this.trees` by creating a new root for `element`

    WARNING: This method mutates the object instance!
  */
  grow(
    element: ExpressionElement,
    queryContext: QueryContext,
    plugins: Plugins.PluginsType
  ) {
    const {queryPathPrefix} = queryContext;
    const hasQueryPathPrefix = queryPathPrefix.length > 0;

    const dataPath = hasQueryPathPrefix
      ? parseDataPath(queryPathPrefix).join('.')
      : EMPTY_DATA_PATH_PLACEHOLDER;

    // Only add query field for bindings and conditional
    // visibility if the node is inside a binding context.
    const queryPathFragments: QueryPath[] = hasQueryPathPrefix
      ? getQueryPathFragments(element, queryContext, plugins)
      : [];

    const children = this.trees[dataPath]
      ? this.trees[dataPath].map((rootNode) => rootNode.subTree)
      : [];

    const rootNode = {id: element.id, children, queryPathFragments} as const;

    this.trees[dataPath] = [{queryPathPrefix, subTree: rootNode}];
  }
}
